import { LoadingOverlay } from '@mantine/core'
import { Map } from 'App/Map/Map'
import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiTokenGetter } from 'utils/apiTokenGetter'
import { pushRoute } from 'utils/pushRoute'
import { Authentication } from './Authentication'
import { ResetPassword } from './Authentication/ResetPassword'
import type { TCurrentTabSelection } from './Map/UserSettingsMenu/types'
import { PrivacyPolicy } from './PrivacyPolicy/PrivacyPolicy'
import { SelfRegistration } from './SelfRegistration/SelfRegistration'
import { Sula } from './Sula/Sula'
import { Tac } from './Tac/Tac'
import { UIExamples } from './UIExamples/UIExamples'

const setModalTab = (tab: TCurrentTabSelection) => {
  smallStore.setState((s) => ({
    ...s,
    showSettingsModal: true,
    settingsModalSelectedTab: tab,
  }))
}

const RequireAuthOrPermission = ({
  children,
  checkType,
}: {
  children: JSX.Element
  checkType?: string
}): JSX.Element => {
  const token = apiTokenGetter()
  const location = useLocation()
  const atAlerts = location.pathname === '/alerts'
  const atReports = location.pathname === '/reports'
  const permissions = userDetailsStore.useSelector((s) => s.permissions)
  const permissionsReady = permissions && Object.keys(permissions).length > 0
  const userCanCreateAlert = Object.keys(permissions || {}).some((p) => /^CREATE_.*_ALERT$/.test(p))
  const userCanCreateReport = Object.keys(permissions || {}).some((p) => p.startsWith('CREATE_REPORT'))

  let permissionCheck: boolean | undefined

  if (checkType === 'alert') {
    permissionCheck = userCanCreateAlert
  } else if (checkType === 'report') {
    permissionCheck = userCanCreateReport
  }

  useEffect(() => {
    if (permissionsReady) {
      if (!permissionCheck && (atAlerts || atReports)) {
        pushRoute('/')
      } else {
        if (atAlerts) setModalTab('alerts')

        if (atReports) setModalTab('reports')
      }
    }
  }, [permissionsReady])

  if (!token) {
    const redirectUri = encodeURI(location.pathname + location.search)

    return <Navigate to={`/login?redirectUri=${redirectUri}`} replace />
  }

  if (!permissionsReady && !token) return <LoadingOverlay visible={true} />

  return children
}

export const AppRoutes = () => {
  return (
    <Routes>
      <Route path="privacy/*" element={<PrivacyPolicy />} />
      <Route path="sula/*" element={<Sula />} />
      <Route path="tac/*" element={<Tac />} />
      <Route path="self-register/*" element={<SelfRegistration />} />
      <Route path="ui-examples/*" element={<UIExamples />} />
      <Route path="reset/*" element={<ResetPassword />} />
      <Route path="choosepassword/*" element={<ResetPassword />} />
      <Route path="login/*" element={<Authentication />} />
      <Route
        path="alerts"
        element={
          <RequireAuthOrPermission checkType="alert">
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="reports"
        element={
          <RequireAuthOrPermission checkType="report">
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="map"
        element={
          <RequireAuthOrPermission>
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="*"
        element={
          <RequireAuthOrPermission>
            <Map />
          </RequireAuthOrPermission>
        }
      />
    </Routes>
  )
}
